import { useEffect } from "react";

// Distance from the container's top/bottom edge (px) where scrolling starts, and the speed at the very edge (px per frame)
const EDGE_SIZE = 80;
const MAX_SPEED = 20;

const isPageScroller = (el) => el === document.scrollingElement || el === document.documentElement;

/** Nearest element (starting with `el` itself) that scrolls vertically; the page when there is none */
const getScrollParent = (el) => {
	for (let node = el; node && node !== document.body; node = node.parentElement) {
		const { overflowY } = window.getComputedStyle(node);
		if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
	}
	return document.scrollingElement || document.documentElement;
};

/**
 * Scrolls the container around `containerRef` while a native HTML5 drag is in progress and the pointer is near
 * its top or bottom edge. Browsers do not do this reliably for drags, least of all inside scrollable elements.
 * @param {boolean} isDragging true from dragstart until dragend
 * @param {{ current: HTMLElement|null }} containerRef element inside the area that should scroll
 */
export default function useDragAutoScroll(isDragging, containerRef) {
	useEffect(() => {
		if (!isDragging) return undefined;

		const scroller = getScrollParent(containerRef.current);
		let pointerY = null;
		let frameId = null;

		// dragover keeps firing while the pointer rests, so this stays current during the drag
		const onDragOver = (e) => { pointerY = e.clientY; };
		// Pointer left the browser window: stop instead of scrolling on a stale position
		const onDragLeave = (e) => { if (!e.relatedTarget) pointerY = null; };

		const step = () => {
			if (pointerY != null) {
				const rect = isPageScroller(scroller) ? { top: 0, bottom: window.innerHeight } : scroller.getBoundingClientRect();
				// Only the visible part of the container counts as its edge
				const top = Math.max(rect.top, 0);
				const bottom = Math.min(rect.bottom, window.innerHeight);
				let speed = 0;
				if (pointerY < top + EDGE_SIZE) speed = -MAX_SPEED * (top + EDGE_SIZE - pointerY) / EDGE_SIZE;
				else if (pointerY > bottom - EDGE_SIZE) speed = MAX_SPEED * (pointerY - (bottom - EDGE_SIZE)) / EDGE_SIZE;
				if (speed) scroller.scrollTop += Math.max(-MAX_SPEED, Math.min(MAX_SPEED, speed));
			}
			frameId = requestAnimationFrame(step);
		};

		document.addEventListener('dragover', onDragOver);
		document.addEventListener('dragleave', onDragLeave);
		frameId = requestAnimationFrame(step);
		return () => {
			document.removeEventListener('dragover', onDragOver);
			document.removeEventListener('dragleave', onDragLeave);
			cancelAnimationFrame(frameId);
		};
	}, [isDragging, containerRef]);
}
